<template>
  <div class="services-settings">
    <section class="settings-card">
      <div v-if="companyStore.services.length" class="table-view__table">
        <div class="table-view__body">
          <div class="table-view__header">
            <div class="table-view__name service">{{ $t("services") }}</div>
            <div>{{ $t("price") }}</div>
            <div>{{ $t("duration") }}</div>
            <div>{{ $t("details") }}</div>
          </div>
          <template v-for="service in companyStore.services">
            <div class="table-view__name">
              <h3>{{ getService(service)?.name || $t("service_not_found") }}</h3>
              <p>{{ service.description || getService(service)?.description }}</p>
            </div>
            <div :data-label="$t('price')">{{ service.price }}</div>
            <div :data-label="$t('duration')">
              {{ $t("booking.duration_minutes", { n: service.duration }) }}
            </div>
            <div class="table-view__actions">
              <VBtn icon @click="openDialog(service)">
                <v-icon small>mdi-pencil</v-icon>
              </VBtn>
              <VBtn color="red" icon @click="openDeleteDialog(service.id!)">
                <v-icon small>mdi-delete</v-icon>
              </VBtn>
            </div>
          </template>
        </div>
      </div>

      <div v-else class="table-view__empty">
        <VIcon icon="mdi-format-list-bulleted" size="28" />
        <p>{{ $t("no_services") }}</p>
      </div>

      <DeleteDialog
        v-model="deleteDialog"
        @delete="deleteService"
        @cancel="serviceId = undefined"
      />
    </section>

    <VBtn class="table-view__add" @click="dialog = true"
      >{{ $t("add_service") }} +</VBtn
    >
    <VDialog width="500" v-model="dialog" :fullscreen="mobile" eager>
      <VCard>
        <VCardTitle>
          <span class="text-h5">{{
            currentService.id ? $t("change_service") : $t("add_service")
          }}</span>
        </VCardTitle>
        <VCardText>
          <v-container>
            <VRow>
              <VCol cols="12">
                <VSelect
                  :items="globalStore.services"
                  item-title="name"
                  item-value="id"
                  :label="$t('service')"
                  required
                  v-model="(currentService.service_id as any)"
                >
                  <template #no-data>
                    <p>{{ $t("service_not_found") }}</p>
                  </template>
                </VSelect>
                <v-text-field
                  :label="$t('description')"
                  variant="underlined"
                  required
                  v-model="currentService.description"
                ></v-text-field>
                <v-text-field
                  :label="$t('price')"
                  type="number"
                  variant="underlined"
                  required
                  v-model="currentService.price"
                ></v-text-field>
                <VSelect
                  :items="[15, 30, 45, 60, 90, 120, 160]"
                  :label="$t('duration_minutes')"
                  required
                  v-model="(currentService.duration as any)"
                >
                </VSelect>
              </VCol>
            </VRow>
          </v-container>
        </VCardText>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog(true)">
            {{ $t("cancel") }}
          </v-btn>
          <v-btn color="#403838" variant="flat" @click="closeDialog(false)">
            {{ $t("save") }}
          </v-btn>
        </v-card-actions>
      </VCard>
    </VDialog>
  </div>
</template>

<script setup lang="ts">
import {
  createCompanyService,
  deleteCompanyService,
  getCompanyServices,
  updateCompanyService,
} from "@/main/services/api.service";
import { useCompanyStore } from "@/main/stores/company.store";
import { useGlobalStore } from "@/main/stores/global.store";
import { CompanyService } from "@/main/types/service.type";
import DeleteDialog from "../delete-popup.vue";
import { onMounted } from "vue";
import { ref } from "vue";
import { useDisplay } from "vuetify";

const companyStore = useCompanyStore();
const globalStore = useGlobalStore();
const { mobile } = useDisplay();

const dialog = ref(false);
const deleteDialog = ref(false);

const currentService = ref<CompanyService>({
  service_id: undefined,
  price: 0,
  duration: 15,
});

const serviceId = ref<number>();

onMounted(async () => {
  try {
    if (companyStore.currentCompany?.id) {
      const companyServices = await getCompanyServices(
        companyStore.currentCompany?.id
      );

      companyStore.services = companyServices;
    }
  } catch (error) {
    console.error(error);
  }
});

const getService = (service: CompanyService) =>
  globalStore.services.find((gs) => gs.id == service.service_id);

const closeDialog = async (clear: boolean) => {
  dialog.value = false;

  if (companyStore.currentCompany) {
    if (currentService.value.id) {
      try {
        const service = await updateCompanyService(
          companyStore.currentCompany!.id!,
          {
            service_id: currentService.value.service_id!,
            description: currentService.value.description,
            price: currentService.value.price,
            duration: currentService.value.duration,
            id: currentService.value.id,
          }
        );

        const index = companyStore.services.findIndex(
          (ser) => ser.id == service.id
        );

        if (index != -1) {
          companyStore.services[index] = {
            ...service,
          };
          clear = true;
        }
      } catch (error) {
        console.error(error);
      }
    } else if (currentService.value.service_id) {
      try {
        const service = await createCompanyService(
          companyStore.currentCompany!.id!,
          {
            service_id: currentService.value.service_id,
            description: currentService.value.description,
            price: currentService.value.price,
            duration: currentService.value.duration,
          }
        );

        companyStore.services.push(service);
        clear = true;
      } catch (error) {
        console.error(error);
      }
    }
  }
  if (clear) {
    currentService.value = {
      service_id: undefined,
      price: 0,
      duration: 15,
    };
    return;
  }
};

const deleteService = async () => {
  if (companyStore.currentCompany && serviceId.value) {
    try {
      await deleteCompanyService(
        companyStore.currentCompany.id!,
        serviceId.value
      );
      companyStore.services = companyStore.services.filter(
        (service) => service.id != serviceId.value
      );
    } catch (error) {
      console.error(error);
    }
  }
  deleteDialog.value = false;
  serviceId.value = undefined;
};

const openDialog = (service: CompanyService) => {
  currentService.value = { ...service };
  dialog.value = true;
};

const openDeleteDialog = (id: number) => {
  deleteDialog.value = true;
  serviceId.value = id;
};
</script>

<style lang="scss" scoped>
.services-settings {
  width: 100%;
  min-width: 0;
}

.settings-card {
  background: #f7f8fa;
  border: 1px solid #eceef1;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
}

.table-view__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 8px;
  padding: 40px 16px;
  color: #696e7c;
  text-align: center;

  p {
    margin: 0;
    font-size: 14px;
  }
}
</style>
